import React, { useContext, useEffect, useState } from "react";
import { Box, Grid } from "@material-ui/core";
import MenuItem from "@material-ui/core/MenuItem/MenuItem";
import { DropzoneArea } from "material-ui-dropzone";
import { tesaWhite } from "../../../styles/colors";
import CustomDialog from "../../../components/Dialog/CustomDialog";
import CustomButton from "../../../components/Button/CustomButton";
import CustomDropdown from "../../../components/Input/CustomDropdown";
import CustomTextField from "../../../components/Input/CustomTextField";
import useReportTemplateStyle from "./ReportTemplatePageStyle";
import useEnv from "../../../hooks/env";
import TestReportTemplateApi from "../../../api/services/TestReportTemplateApi";
import downloadFile from "../../../functions/downloadFile.function";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import { IPrepareParamsForApi, IReportTemplateUploadDialogProps } from "./interfaces";
import { FILE_TYPES, FILE_TYPES_MAPPING, LANGUAGES, LANGUAGES_MAPPING } from "./common";
import GalleryButton from "../../../components/Gallery/button/GalleryButton";

const KB = 1024;
const MB = 1024 * KB;

const prepareParamsForApi = ({ selectedFile, fileType, language, requestId = "" }: IPrepareParamsForApi) => {
  const fileTypeMapped = FILE_TYPES_MAPPING[fileType];
  const languageMapped = LANGUAGES_MAPPING[language];
  const formData = new FormData();
  formData.append("file", selectedFile);
  formData.append("fileType", fileTypeMapped);
  formData.append("language", languageMapped);
  formData.append("requestId", requestId);
  formData.append("name", selectedFile.name);
  return formData;
};

const ReportTemplateUploadDialog: React.FC<IReportTemplateUploadDialogProps> = (props) => {
  const classes = useReportTemplateStyle(props);
  const { open, onClose } = props;
  const { api, environment } = useEnv();
  const testReportTemplateApi = new TestReportTemplateApi(api);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const [language, setLanguage] = useState<string>("");
  const [fileType, setFileType] = useState<string>("");
  const [leapId, setLeapId] = useState<string>("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [togglePublishButton, setTogglePublishButton] = useState<boolean>(true);

  const [verifying, setVerifying] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const resetState = () => {
    setFileType("");
    setLanguage("");
    setSelectedFiles([]);
    setLeapId("");
    setTogglePublishButton(true);
  };

  const enablePublishButton = () => setTogglePublishButton(false);

  const disablePublishButton = () => setTogglePublishButton(true);

  const handleVerify = () => {
    if (!fileType || !language || !leapId) {
      showSnackbar("Enter file type or language or Leap ID", "error");
      return;
    }
    const formData = prepareParamsForApi({
      selectedFile: selectedFiles[0],
      fileType,
      language,
      requestId: leapId,
    });
    setVerifying(true);
    testReportTemplateApi
      .verifyTestReportTemplate(formData)
      .then((response) => {
        downloadFile(response);
        enablePublishButton();
      })
      .catch(handleHttpError)
      .finally(() => setVerifying(false));
  };

  const handlePublish = () => {
    if (!fileType || !language) {
      showSnackbar("Enter file type or language", "error");
      return;
    }

    const formData = prepareParamsForApi({
      selectedFile: selectedFiles[0],
      fileType,
      language,
    });

    setPublishing(true);
    testReportTemplateApi
      .publishTestReportTemplate(formData)
      .then(() => {
        showSnackbar("Report Template Uploaded", "success");
        onClose();
      })
      .catch(handleHttpError)
      .finally(() => setPublishing(false));
  };
  const handleOnChangeLanguage = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    setLanguage(e.target.value as string);
  };

  const handleOnChangeFileType = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    setFileType(e.target.value as string);
  };

  const handleOnChangeLeapId = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    setLeapId(e.target.value as string);
  };

  const setDefaultRequestIdBasedOnEnv = () => {
    if (environment === "beta") {
      setLeapId("1E2ZKT");
    }
  };

  useEffect(() => {
    resetState();
    setDefaultRequestIdBasedOnEnv();
  }, [open]);

  return (
    <CustomDialog onClose={onClose} open={open} disableBackdropClick>
      <>
        <Grid container>
          <h2 className={classes.header}>Report Template</h2>
          <Grid item lg={12} md={12} sm={12} xs={12} className={classes.MuiDropzoneArea}>
            <DropzoneArea
              clearOnUnmount
              onChange={setSelectedFiles}
              showAlerts={false}
              getDropRejectMessage={() => {
                return "Upload not possible. The file size is too big.";
              }}
              getFileLimitExceedMessage={() => "Upload not possible. Maximum file limit is 25."}
              maxFileSize={500 * MB}
              showPreviewsInDropzone
              useChipsForPreview
              previewChipProps={{ style: { backgroundColor: tesaWhite } }}
              filesLimit={1}
              onDelete={disablePublishButton}
            />
          </Grid>
          <Grid item xs={12} className={classes.textField}>
            <CustomDropdown
              mandatory
              name="language"
              id="language"
              type="language"
              labelText="Language"
              value={language}
              onChange={handleOnChangeLanguage}
            >
              {LANGUAGES.map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {lang.toUpperCase()}
                </MenuItem>
              ))}
            </CustomDropdown>
          </Grid>
          <Grid item xs={12} className={classes.textField}>
            <CustomDropdown
              mandatory
              name="fileType"
              id="fileType"
              labelText="File Type"
              value={fileType}
              onChange={handleOnChangeFileType}
            >
              {FILE_TYPES.map((fileTyp) => (
                <MenuItem key={fileTyp} value={fileTyp}>
                  {fileTyp.toUpperCase()}
                </MenuItem>
              ))}
            </CustomDropdown>
          </Grid>
          <Grid item xs={12} className={classes.textField}>
            <CustomTextField mandatory label="Leap ID" name="leapId" value={leapId} onChange={handleOnChangeLeapId} />
          </Grid>
          <Grid className={classes.buttonContainer} xs={12} container direction="row" justify="flex-end">
            <Box marginLeft={2}>
              <CustomButton text="Cancel" onClick={onClose} />
            </Box>
            <Box marginLeft={2}>
              <GalleryButton
                text="Publish"
                loading={publishing}
                onClick={handlePublish}
                disabled={togglePublishButton}
              />
            </Box>
            <Box marginLeft={2}>
              <GalleryButton text="Verify" loading={verifying} onClick={handleVerify} disabled={!selectedFiles} />
            </Box>
          </Grid>
        </Grid>
      </>
    </CustomDialog>
  );
};

export default ReportTemplateUploadDialog;
